import type {
  AccountState,
  CalibrationState,
  DashboardClinicianData,
  LiveScoring,
  SeatPoolData,
  TermScope,
} from "@/hooks/queries/org-admin-dashboard-types";
import { formatDate } from "@/lib/format-date";

// Story 14.1 — pure client-side derivations for the org_admin dashboard
// (Screen 25): the extra KPI tile, the expiring-subscriptions banner count,
// and the Seat health percentage are ALL computed from the envelope — no
// extra backend surface. Kept apart from the screen component so the file
// exporting the component stays fast-refresh-clean and the helpers unit-test
// without rendering.

/** Window for the "subscriptions ending soon" banner (mockup copy: 30 days). */
export const SUBSCRIPTION_EXPIRY_WINDOW_DAYS = 30;

/**
 * FR83 in one sentence, owned HERE so the two surfaces that must say it cannot
 * drift apart: Story 14.11's capacity banner (where an admin reads it while
 * planning) and Story 14.6's removal confirmation (where they act on it). The
 * second half is the one people get wrong, so the copy says it outright instead
 * of leaving "does the slot come back?" to be inferred.
 *
 * Re-scoped by Story 21.2 (FR87, AC11) from *"for the remainder of the contract
 * term"* to *"within the current contract term"*. The old wording was true when
 * a term never ended in practice; a renewal now closes one and opens another,
 * resetting the pool to the live headcount, so "the remainder" read as a
 * promise that the seat is gone forever.
 *
 * **The re-invitation clause was CUT by the PM (PR #836).** The sentence used to
 * end *"does not free it for re-invitation"*, which said the wrong thing twice
 * over. Re-invitation to the same address is refused in EVERY term, not just
 * this one (`duplicate_email`), and the same person coming back is CITS
 * attaching the existing account (FR88) rather than an invitation at all — so
 * scoping "not for re-invitation" to the current term implied that a later term
 * would let it through. What the seat actually does is not return to the pool
 * while this term runs, and that is now all the sentence claims.
 *
 * **What it deliberately does NOT mention: 22.3's correction window (AC11, D9).**
 * That release is a super_admin correction on an attachment. An org_admin can
 * neither perform it nor request it, and offering *"unless someone reverses it
 * within a few days"* in a removal-confirmation banner would read as exactly the
 * seat-recovery route the client's narrow approval was written to exclude. What
 * this sentence asserts about the reader's OWN action stays unconditionally
 * true. The exception belongs to the surface whose reader can act on it —
 * 22.3's reversal dialog. The two super_admin sites this constant also reaches
 * (`OrgDetailScreen.tsx`, `admin-orgs-error.ts`'s ORG-007 body) are a genuinely
 * open question, and `seam-212-seat-stays-consumed-sentence` hands it to 21.1
 * rather than settling it here.
 */
export const SEAT_STAYS_CONSUMED_SENTENCE =
  "A seat once assigned stays consumed within the current contract term — removing a clinician does not return it to the pool.";

/**
 * What the roster on BOTH org_admin screens contains, owned here for the same
 * reason {@link SEAT_STAYS_CONSUMED_SENTENCE} is: Screen 25 and Screen 26 list
 * the rows behind the same Consumed tile, so a reader who meets a "Removed" pill
 * deserves the same explanation on either screen.
 *
 * Re-scoped by Story 21.2 (FR87, AC10) to describe what the roster now
 * CONTAINS. A renewal closes a term and resets the pool, so the list is no
 * longer "every seat this organization has ever consumed": it is the current
 * term's seats — the rows the Consumed tile is made of — plus the seats a
 * closed term left behind, which are still listed and are marked as released.
 * The previous wording promised a reconciliation the tile stopped performing at
 * the first boundary.
 */
export const ROSTER_SCOPE_SENTENCE =
  "Every seat consumed in the current contract term — including invitations that have not been accepted yet, and removed clinicians whose seat stays consumed for the rest of the term — plus seats released when an earlier term closed, shown as released.";

/**
 * Story 21.2 (FR87, AC9) — the term start, in ONE shared constant beside the
 * two sentences above, for the same anti-drift reason they are here: Screen 25
 * renders it in the Seat health card and Screen 26 under the StatsGrid, and the
 * two must not word the same fact differently.
 *
 * **It states the term start and nothing else, and that restraint is the
 * decision (D9).** It must not say — or let a reader infer — that a renewal is
 * WHY the Consumed figure moved. A super_admin correction inside 22.3's window
 * drops the same figure mid-term with no boundary and no renewal behind it, and
 * an org_admin who meets that on a Tuesday must not be shown copy asserting
 * something that did not happen. So: a date and a term, no verb of causation,
 * and no word of the renewal vocabulary.
 *
 * Returns `null` rather than a placeholder when the organization has no term
 * start, so the caller renders NOTHING — `formatDate` would answer "—", and a
 * line reading "Current term started —" states an absence as if it were a fact
 * about the term. The guard is here rather than at each call site so both
 * screens cannot disagree about it.
 *
 * Formatted with the shared `formatDate` and deliberately not a third local
 * date-formatting variant: `contract-notice.ts` is already a redundant second
 * one, and this constant does not add to it.
 */
export function termStartLine(
  currentTermStartedAt: string | null | undefined,
): string | null {
  if (!currentTermStartedAt) return null;
  return `Current term started ${formatDate(currentTermStartedAt)}`;
}

/**
 * Live-coverage scope for the two dashboard STATISTICS — no longer the roster's
 * scope, and the history is the reason this comment is long.
 *
 * Story 14.6 (D4) widened the SHARED `/org-admin/dashboard` payload to carry
 * removed rows, because Screen 26 has to show dead slots for its Consumed tile
 * to reconcile. Screen 25 applied this filter ONCE where the array entered the
 * component, on the reading that its roster answers "who is working in my
 * organization" and has no reconciliation of its own to protect. That reading
 * missed the Consumed tile and the Seat-health bar, which are on Screen 25 too
 * and read straight from `seat_pool` — so the dashboard showed "Consumed 6" over
 * five rows: the exact failure mode D4 was written to eliminate, on the screen
 * the org_admin sees first.
 *
 * The rule that replaced it (PDM 2026-08-13): every surface showing a seat
 * COUNTER shows the rows that make it up, removed ones included, each in its own
 * state. So the roster is unfiltered on both screens, and this filter belongs to
 * the two numbers that genuinely are statements about live coverage — the
 * calibration-complete KPI and the expiring-subscriptions banner. Both apply it
 * THEMSELVES rather than trusting a caller to hand them a pre-filtered array,
 * because trusting the caller is precisely what broke.
 *
 * `blocked` (2026-08-13) is excluded on the SAME sentences: a CITS-blocked
 * clinician cannot authenticate at all, so counting them into the
 * calibration-complete KPI misreports them as live-ready capacity, and this
 * table has no State column to distinguish them. Screen 26 is where a blocked
 * holder is visible — with its "Blocked (CITS)" pill — because that screen's
 * job is seat accounting, not live coverage.
 */
export function excludeNonWorking(
  clinicians: DashboardClinicianData[],
): DashboardClinicianData[] {
  return clinicians.filter(
    (c) => c.account_state !== "removed" && c.account_state !== "blocked",
  );
}

/**
 * Count of roster rows whose calibration is complete — the 4th KPI tile.
 * A removed clinician's calibration is not coverage this organization still
 * has, so the row is scoped out here (see {@link excludeNonWorking}).
 */
export function countCalibrationComplete(
  clinicians: DashboardClinicianData[],
): number {
  return excludeNonWorking(clinicians).filter(
    (c) => c.calibration_state === "complete",
  ).length;
}

/**
 * Count of roster rows whose subscription ends within the next
 * {@link SUBSCRIPTION_EXPIRY_WINDOW_DAYS} days (inclusive of today,
 * exclusive of already-ended). Date-only comparison — `now` is floored to
 * local midnight so an afternoon render doesn't drop a today-dated expiry.
 *
 * Removed rows are scoped out (see {@link excludeNonWorking}): 14.6 froze a dead
 * slot's dates at its last known state, so its end date is a historical fact,
 * not a renewal anyone is waiting on.
 */
export function countExpiringSoon(
  clinicians: DashboardClinicianData[],
  now: Date,
): number {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const windowEnd = new Date(today);
  windowEnd.setDate(windowEnd.getDate() + SUBSCRIPTION_EXPIRY_WINDOW_DAYS);
  return excludeNonWorking(clinicians).filter((c) => {
    if (!c.subscription_end_date) return false;
    if (!isRenewable(c.subscription_status)) return false;
    const end = new Date(`${c.subscription_end_date}T00:00:00`);
    return end >= today && end <= windowEnd;
  }).length;
}

/**
 * GH #723 — whether this subscription's period end is a RENEWAL an admin should
 * be warned about, or merely the date a finished subscription stopped.
 *
 * Only `active` qualifies. That is the same predicate
 * `IndividualSubscriptionProvider` uses for "live", so the banner cannot claim a
 * renewal is coming for someone the entitlement chain already treats as
 * inactive. The exclusions are deliberate rather than accidental, and each is a
 * different kind of "not a renewal":
 *
 *   • `canceled` / `incomplete_expired` — already over. This is the literal
 *     #723 defect: a cancelled subscription whose stored period end happens to
 *     land inside the window raised a warning about a renewal nobody was
 *     waiting for.
 *   • `pending_calibration` / `incomplete` — never began.
 *   • `past_due` / `unpaid` — Stripe is still dunning the card and the
 *     subscription can recover, but the period end is not a renewal horizon.
 *     A dunning warning is a different surface, and this product has none.
 *   • `trialing` / `paused` — live in their own way; the date is still not a
 *     renewal.
 *
 * `undefined` — the key absent entirely — is deploy skew against a pre-#723
 * API, and is treated as renewable so the count keeps behaving as it did
 * before. Narrowing on a fact the server never sent would silently drop
 * warnings the admin used to receive. `accountStatePill` splits the two
 * absences the same way, and for the same reason.
 */
function isRenewable(status: DashboardClinicianData["subscription_status"]): boolean {
  if (status === undefined) return true;
  return status === "active";
}

/** Consumed share of capacity, clamped 0–100 (no divide-by-zero, no >100%
 * bar even if a data anomaly ever put consumed above capacity). */
export function seatHealthPercent(seatPool: SeatPoolData): number {
  if (seatPool.capacity <= 0) return 0;
  return Math.min(100, Math.round((seatPool.consumed / seatPool.capacity) * 100));
}

// UX D3 state-flag pills — label + variant per wire state. The `in_progress`
// label here is the COUNTLESS fallback; with a count it becomes "{N}/{M} cases"
// (Story 14.3, see calibrationPill).
export const CALIBRATION_PILL: Record<
  CalibrationState,
  { variant: "ok" | "pending" | "blocked"; label: string }
> = {
  complete: { variant: "ok", label: "Complete" },
  in_progress: { variant: "pending", label: "In progress" },
  not_started: { variant: "blocked", label: "Not started" },
};

/**
 * Pill for a roster row's calibration state.
 *
 * An `in_progress` row with a known count renders the UX §13.5 literal
 * "{N}/{M} cases" — no spaces around the slash, and the denominator comes from
 * the payload's `required_case_count`, never a hardcoded 5.
 *
 * Two defensive degradations, both deliberate (additive-field deploy skew,
 * Rule 06): an `in_progress` row missing either number keeps the generic
 * "In progress" label, and an UNKNOWN state falls back to the "Not started"
 * pill instead of crashing the render (`CALIBRATION_PILL[bad]` would yield
 * `undefined.variant`).
 */
export function calibrationPill(
  state: CalibrationState,
  completedCases?: number,
  requiredCaseCount?: number,
) {
  const pill = CALIBRATION_PILL[state] ?? CALIBRATION_PILL.not_started;
  // `!= null` on purpose — it catches BOTH undefined (the key the backend omits
  // today) and an explicit null, which is what a backend that ever dropped the
  // field-scoped serializer would send. `!== undefined` lets null through and
  // renders the literal label "null/5 cases".
  if (state === "in_progress" && completedCases != null && requiredCaseCount != null) {
    return { ...pill, label: `${completedCases}/${requiredCaseCount} cases` };
  }
  return pill;
}

// ---------------------------------------------------------------------------
// Story 14.11 — Screen 26 (Seats & allocation) derivations.
// ---------------------------------------------------------------------------

/**
 * The "Awaiting activation" tile (AC5). Defined as EXACTLY the count of rows
 * whose `account_state` is `invited` — the epics literal — so the tile can never
 * drift from the State column beside it, and no new top-level payload key is
 * needed to carry a number the roster already states.
 *
 * The label was corrected 2026-08-12 and the DERIVATION deliberately was not.
 * `invite_clinician` mints `is_active = is_legacy`, so a legacy invitee is
 * active — displayed "Assigned" — from the moment they are invited, and was
 * never in this count. Under the old label "Pending invites" that read as an
 * undercount of outstanding invitations; under this one the number is exactly
 * what it says, accounts not yet activated.
 *
 * Story 14.6 needed no change here: because `removed` OUTRANKS `invited` on the
 * wire, a removed invitee arrives as `removed` and drops out of this count by
 * construction. That is the whole reason the state is one discriminator rather
 * than a second boolean field. `blocked` (2026-08-12) rides in on the same
 * property — a blocked account arrives as `blocked`, never as `invited`, so the
 * tile still counts only people the admin is genuinely waiting on.
 */
export function countPendingInvites(
  clinicians: DashboardClinicianData[],
): number {
  return clinicians.filter((c) => c.account_state === "invited").length;
}

/**
 * The State column (D3.1). The mockup's "Seat" column is repurposed: every
 * representable roster row HAS a seat, so its Assigned / "Not assigned" / "N/A"
 * options collapse into the ACCOUNT state.
 *
 * `removed` (Story 14.6, FR83) has no mockup — it is spec-driven, the 14.12
 * precedent. It takes the `blocked` variant: the slot is dead, and the muted
 * treatment is the same visual language the Live-scoring "Blocked" pill uses.
 *
 * `blocked` is spec-driven for the same reason, and its label NAMES THE BLOCKER
 * (PDM 2026-08-12). An org_admin cannot unblock an account — only CITS can — so
 * a bare "Blocked" would leave the one question this column exists to answer
 * ("why can't I act on this person?") unanswered, and the admin would go
 * looking for the switch they do not have.
 */
export const ACCOUNT_STATE_PILL: Record<
  AccountState,
  { variant: "ok" | "pending" | "blocked"; label: string }
> = {
  active: { variant: "ok", label: "Assigned" },
  invited: { variant: "pending", label: "Invited" },
  removed: { variant: "blocked", label: "Removed" },
  blocked: { variant: "blocked", label: "Blocked (CITS)" },
};

/**
 * Whether a roster row still offers member actions. A removed row offers NONE:
 * nothing on a dead slot is editable, and the API refuses every member action
 * against it with ORG-008 anyway (the resolver's predicate is "ACTIVE seat in
 * the actor's org"). Rendering an Edit button that can only ever 403 would be
 * an affordance that lies.
 *
 * A `blocked` row DOES stay actionable, and since Story 15.8 that is no longer
 * because the server would perform the actions — it refuses all four with
 * 409 ORG-017. The affordance survives on the opposite argument: Edit is the
 * only surface that EXPLAINS the refusal. Behind it the dialog names CITS as
 * the blocker on every disabled control, and the read-only Calibration and
 * Subscription fields — which no gate touches — stay reachable. Hiding the
 * button would remove the explanation along with the write, leaving an admin
 * with a "Blocked (CITS)" pill and nowhere to learn what it means for them.
 *
 * So the asymmetry with `removed` holds for a different reason at each end: a
 * removed row has nothing left to say, while a blocked row has something the
 * dialog is the only place to say it.
 */
export function isRowActionable(
  clinician: Pick<DashboardClinicianData, "account_state">,
): boolean {
  return clinician.account_state !== "removed";
}

/**
 * Spec-driven pill for a state this bundle does not recognise. NOT a fourth
 * roster state — nothing on the wire ever equals this; it is what the screen
 * says when the backend has told it something newer than it understands.
 */
export const UNKNOWN_ACCOUNT_STATE_PILL = {
  variant: "blocked",
  label: "Unknown state",
} as const;

/**
 * Story 21.2 (FR87, AC8) — a removed row whose seat belonged to a term the
 * boundary has already closed. Spec-driven with NO mockup, the same
 * `contract-notice.ts` precedent the `removed` and `blocked` pills above take.
 *
 * NOT a fifth `AccountState`: nothing on the wire equals this. It is the
 * `removed` pill re-labelled by the second, orthogonal discriminator, which is
 * why it lives beside {@link ACCOUNT_STATE_PILL} rather than inside it — that
 * map is keyed exhaustively by `AccountState` and a fifth entry would not type.
 *
 * "Released", not "Removed": both are dead slots, but the reader's question
 * differs. A `removed` row still occupies one of the seats their Consumed tile
 * counts; a released one does not, and telling them apart is the whole reason
 * the closed-term rows stay on the roster. The `blocked` VARIANT is shared with
 * `removed` on purpose — the slot is dead either way and the muted treatment is
 * the same visual language.
 *
 * **A SECOND population reaches this pill as of Story 22.4 (AC18), and
 * "(past term)" is not true of it.** `_resolve_term_scope` now answers `closed`
 * for any row carrying 22.3's `released_at`, including a reversal performed
 * inside the CURRENT term's correction window — so an org_admin who undoes a
 * mistaken attach this morning sees the seat labelled as belonging to a term
 * that has not ended. The `Released` half is still exactly right (the seat came
 * back and the Consumed tile no longer counts it); only the parenthetical
 * over-claims.
 *
 * **Deliberately NOT repaired here.** The frontend cannot tell the two apart:
 * `released_at` and `seat_removed_at` are both folded into `term_scope` and
 * dropped before the wire, because 14.6 ruled a colleague's seat dates an HR
 * fact and `P0-ORG-04`'s exact-key-set test pins their absence. So the only
 * available repairs are re-labelling this constant or putting a seat timestamp
 * on the wire — and this story's boundary row 12 rejects both: 21.2 owns this
 * copy, and AC5 owns that absence. Filed `[→human:ux]` instead, in the same
 * family as the open `seam-223-what-a-reversed-clinician-is-told`. Whoever
 * settles it needs either a third `TermScope` literal from the server or a
 * label true of both populations.
 */
export const RELEASED_PAST_TERM_PILL = {
  variant: "blocked",
  label: "Released (past term)",
} as const;

/**
 * Two different absences, two different answers (corrected 2026-08-12).
 *
 * MISSING key → "Assigned", fail-SOFT and unchanged: a pre-14.11 backend sends
 * no `account_state` at all, the row still has a seat, and this is the same
 * display the backend itself falls back to when the authz projection is
 * missing (Rule 06 additive-field skew).
 *
 * UNRECOGNISED value → fail-CLOSED, matching the sibling `liveScoringPill`.
 * The backend deliberately distinguished this row from `active`, and the enum
 * only ever grows toward states that are WORSE than active — `removed`,
 * `blocked`, whatever comes next. `blocked` is what made this concrete: an old
 * bundle meeting the new literal rendered a CITS-blocked account as "Assigned",
 * the most reassuring label available, for the row most needing attention.
 * "Unknown state" claims nothing the client cannot back up.
 *
 * {@link isRowActionable} deliberately does NOT follow: the server is the
 * authority on what it will refuse (ORG-008), and disabling Edit on an
 * unrecognised state would invent a refusal that may not exist.
 *
 * **`termScope` (Story 21.2, AC8) is a second, orthogonal layer applied AFTER
 * the `account_state` lookup**, and it changes exactly one cell of the matrix:
 * `removed` + `closed` reads {@link RELEASED_PAST_TERM_PILL}. Three properties
 * of the ordering are load-bearing:
 *
 * * an UNRECOGNISED `account_state` still wins, whatever the scope says — the
 *   client does not know what the row is, so it may not claim to know that the
 *   row's seat came back;
 * * an ABSENT `termScope` behaves as `current`, the deploy-skew fallback the
 *   optional wire field exists for (a pre-21.2 API sends no such key, and every
 *   row it sends genuinely was current-term at the time);
 * * `active` / `invited` / `blocked` with scope `closed` is not reachable — a
 *   live or pending seat has no removal to have been left behind by — and falls
 *   through to the `account_state` label rather than being special-cased, so an
 *   unreachable combination renders the state the server DID assert.
 *
 * The label keys on the shared server-derived discriminator and never on a
 * client-side date comparison: the seat timestamps that would be needed are
 * deliberately off the wire (AC5), and a second derivation is the drift this
 * whole field exists to prevent.
 */
export function accountStatePill(state?: AccountState, termScope?: TermScope) {
  if (state === undefined) return ACCOUNT_STATE_PILL.active;
  const pill = ACCOUNT_STATE_PILL[state];
  if (pill === undefined) return UNKNOWN_ACCOUNT_STATE_PILL;
  if (state === "removed" && termScope === "closed") {
    return RELEASED_PAST_TERM_PILL;
  }
  return pill;
}

/** The Live scoring column (D3.2) — rendered 1:1 from the BE-derived flag. */
export const LIVE_SCORING_PILL: Record<
  LiveScoring,
  { variant: "ok" | "blocked"; label: string }
> = {
  enabled: { variant: "ok", label: "Enabled" },
  blocked: { variant: "blocked", label: "Blocked" },
};

/**
 * Fail-CLOSED fallback, deliberately the opposite of {@link accountStatePill}:
 * an unknown value must never render as permission to score live, so anything
 * the enum does not name reads "Blocked".
 */
export function liveScoringPill(state?: LiveScoring) {
  return LIVE_SCORING_PILL[state as LiveScoring] ?? LIVE_SCORING_PILL.blocked;
}

/** Modal copy when a member has no subscription row yet (W3 row 15). */
export const PRE_CALIBRATION_SUBSCRIPTION_COPY =
  "Pre-calibration — activates after calibration completes";

/**
 * Story 22.4 AC11 — the copy for a seat whose access comes from the
 * organization rather than from a `billing.subscriptions` row of its own.
 * It borrows `SubscriptionSection`'s B2B vocabulary on purpose, so the seated
 * clinician's own Profile and their org_admin's Edit-member dialog name the same
 * arrangement with the same NOUN PHRASE — "your organization's CITS contract".
 * The verbs differ and are meant to: Profile addresses the clinician in a full
 * sentence (`B2B_HELPER_COPY`, "Your subscription is provided by …"), while this
 * is a value rendered into a one-line read-only field, where a full sentence
 * would not fit the row. Flagged for UX either way — the exact wording is theirs
 * to settle, and only the shared noun phrase is load-bearing here.
 */
export const ORG_COVERED_SUBSCRIPTION_COPY =
  "Covered by your organization's CITS contract";

/**
 * The Edit-member modal's read-only Subscription field (W3 row 15). Seat
 * clinicians without a `billing.subscriptions` row legitimately carry nulls;
 * a partial range still shows what IS known rather than hiding the field.
 *
 * **Which sentence the empty case gets is decided by calibration, not by the
 * nulls (Story 22.4 AC11).** Two different populations reach the same empty
 * shape and only one of them is pre-calibration:
 *
 * * calibration NOT complete — the seat genuinely activates on completion, so
 *   `PRE_CALIBRATION_SUBSCRIPTION_COPY` is the true sentence and is unchanged;
 * * calibration complete — the fields are empty because this clinician's access
 *   is the organization's contract, not a subscription of their own. That is
 *   the pure B2B invitee, and (since Story 22.1) the ATTACHED clinician, whose
 *   three individual-billing fields `_ROSTER_SQL` now deliberately withholds.
 *   Telling their org_admin they are "pre-calibration" would be a false
 *   statement about a clinician who is calibrated and scoring today.
 */
export function memberSubscriptionSummary(
  clinician: Pick<
    DashboardClinicianData,
    "subscription_start_date" | "subscription_end_date" | "calibration_state"
  >,
): string {
  const { subscription_start_date: start, subscription_end_date: end } =
    clinician;
  if (start && end) return `${start} – ${end}`;
  if (end) return `Ends ${end}`;
  if (start) return `Started ${start}`;
  return clinician.calibration_state === "complete"
    ? ORG_COVERED_SUBSCRIPTION_COPY
    : PRE_CALIBRATION_SUBSCRIPTION_COPY;
}

/**
 * Org-role filter options (boundary #6): the DISTINCT labels present on the
 * loaded rows, not the static invite-form list — an org that never used a
 * label should not be offered a filter that can only ever return nothing.
 */
export function orgRoleFilterOptions(
  clinicians: DashboardClinicianData[],
): string[] {
  return [...new Set(clinicians.map((c) => c.organization_role_label))].sort(
    (a, b) => a.localeCompare(b),
  );
}

/** Case-insensitive substring match over name and email (AC7). */
export function filterClinicians(
  clinicians: DashboardClinicianData[],
  { search, orgRole }: { search: string; orgRole: string },
): DashboardClinicianData[] {
  const needle = search.trim().toLowerCase();
  return clinicians.filter((c) => {
    if (orgRole && c.organization_role_label !== orgRole) return false;
    if (!needle) return true;
    const haystack = [c.first_name, c.last_name, c.email]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  });
}
